import { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useRootDispatch, useRootSelector } from '../../../../../store/hooks';
import { UseFormReturn } from 'react-hook-form';
import { DiscoveryContentTypes } from '../../../../../constants';
import {
  fetchSitePageDiscovery,
  fetchSitePageDiscoveryData,
  Discovery,
  updatePreloadedProductsList,
  SiteDiscoveryValues,
  SiteFacetsConfigData,
} from '../../../../../store/features/discovery';
import { UseInfiniteScrollProps, useKeywordSearch } from '../../../../../hooks';
import qs from 'qs';
import { selectPageConfig } from '../../../../../store/features/pageConfig';

export interface UseSiteSearchFilterProps<T> {
  form: UseFormReturn<T>;
  contentType: DiscoveryContentTypes;
  defaultValues: T;
  initialValue?: string;
  searchQueryData?: SiteFacetsConfigData;
}

export function useSiteSearchFilter<T extends Partial<SiteDiscoveryValues>>(
  props: UseSiteSearchFilterProps<T>,
) {
  const {
    form: { reset, getValues, setValue },
    contentType,
    initialValue,
    defaultValues,
    searchQueryData,
  } = props;
  const { siteId, siteTypePath } = useRootSelector(selectPageConfig);
  const history = useHistory();
  const dispatch = useRootDispatch();
  const { pathname } = useLocation();
  const [loading, setLoading] = useState<boolean>(false);

  const changeContentTypeFilterHandler = useCallback(
    (type: DiscoveryContentTypes[]) => {
      const { searchInput } = getValues();

      reset({
        ...defaultValues,
        type,
        searchInput,
      });
    },
    [defaultValues, getValues, reset],
  );

  const keywordSearchHandler = useCallback(
    (searchInput: string) => {
      const values = getValues();

      reset({
        ...defaultValues,
        ...values,
        searchInput,
      });
    },
    [defaultValues, getValues, reset],
  );

  const keywordSearchProps = useKeywordSearch(
    keywordSearchHandler,
    initialValue,
  );

  const { handleReset: resetSearchValueHandler } = keywordSearchProps;

  const changeFilterHandler = useCallback(
    (selectedFilterValues: T, name?: string) => {
      // type.0 means radio buttons
      const typeFilter = name && ['type', 'type.0'].includes(name);

      if (typeFilter && selectedFilterValues.type) {
        return changeContentTypeFilterHandler(selectedFilterValues.type);
      }

      const values = {
        ...defaultValues,
        ...selectedFilterValues,
        ...searchQueryData,
      };

      const stringifyValues = qs.stringify(values, {
        addQueryPrefix: true,
        encode: true,
      });

      if (stringifyValues) {
        history.push(stringifyValues);
      } else {
        history.replace(pathname);
      }

      setLoading(true);

      dispatch(
        fetchSitePageDiscovery({
          values,
          siteTypePath,
          siteId,
        }),
      ).finally(() => {
        setLoading(false);
      });
    },
    [
      defaultValues,
      history,
      dispatch,
      changeContentTypeFilterHandler,
      siteTypePath,
      siteId,
      pathname,
      searchQueryData,
    ],
  );

  const loadMoreFilterResultsHandler: UseInfiniteScrollProps['fetchMoreData'] =
    useCallback(
      (page) => {
        const values = getValues();

        return dispatch(
          fetchSitePageDiscoveryData({
            values: { ...values, page },
            siteTypePath,
            siteId,
          }),
        ).then(({ payload, meta: { requestStatus } }) => {
          if (requestStatus === 'fulfilled') {
            dispatch(
              updatePreloadedProductsList({
                data: payload as Discovery,
                contentType,
              }),
            );
          }
        });
      },
      [dispatch, getValues, contentType, siteTypePath, siteId],
    );

  const resetFilterHandler = useCallback(() => {
    reset(defaultValues);
    resetSearchValueHandler();
  }, [defaultValues, reset, resetSearchValueHandler]);

  const resetTextFilterHandler = useCallback(() => {
    // @ts-ignore
    setValue('searchInput', '');
    resetSearchValueHandler();
  }, [resetSearchValueHandler, setValue]);

  return {
    loading,
    changeFilterHandler,
    resetFilterHandler,
    resetTextFilterHandler,
    loadMoreFilterResultsHandler,
    keywordSearchProps,
  };
}
